import { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'
import { MAX_BODY_CHARS } from '../../lib/files'
import type { PendingUpload } from './uploadsStore'

interface Props {
  /** The photos and videos picked, going up meanwhile. */
  uploads: PendingUpload[]
  /** The message's words, sent with them. */
  caption: string
  onCaption: (caption: string) => void
  /** Everything is up and the words aren't too long. */
  canSend: boolean
  onSend: () => void
  /** Drops the photos picked. */
  onCancel: () => void
}

function titleFor(uploads: PendingUpload[]): string {
  const videos = uploads.filter((u) => u.kind === 'video').length
  const noun = videos === uploads.length ? 'video' : videos === 0 ? 'photo' : 'item'
  return uploads.length === 1 ? `Send ${noun}` : `Send ${uploads.length} ${noun}s`
}

/**
 * Photos and videos picked to send, full screen, as WhatsApp shows them: one at a time, swiped between, with one caption and
 * Send at the foot. They go up while this shows; Send waits for them. Nothing here takes the cursor as it opens, so it can
 * slide up like a phone's own screens and the keyboard only comes for the caption.
 */
export function PhotoPreview({ uploads, caption, onCaption, canSend, onSend, onCancel }: Props) {
  const strip = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)
  const showing = Math.min(index, uploads.length - 1)

  return (
    <Dialog chrome="bar" open onClose={onCancel} title={titleFor(uploads)} titleId="photo-preview-title" closeLabel="Cancel" className="photo-preview slides-up">
      <div
        ref={strip}
        className="photo-preview-strip"
        onScroll={(event) => {
          const el = event.currentTarget
          setIndex(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)))
        }}
      >
        {uploads.map((upload) => (
          <div key={upload.localId} className="photo-preview-slide">
            {upload.kind === 'video' ? <PreviewVideo upload={upload} /> : <img src={upload.previewUrl ?? undefined} alt={upload.fileName} />}
          </div>
        ))}
      </div>
      {uploads.length > 1 ? (
        <p className="photo-preview-position" aria-live="polite">
          {showing + 1} of {uploads.length}
        </p>
      ) : null}
      <form
        className="photo-preview-foot"
        onSubmit={(event) => {
          event.preventDefault()
          if (canSend) onSend()
        }}
      >
        <textarea
          className="photo-preview-caption"
          rows={1}
          maxLength={MAX_BODY_CHARS}
          placeholder="Add a caption"
          aria-label="Add a caption"
          value={caption}
          onChange={(event) => onCaption(event.target.value)}
        />
        <button type="submit" className="button primary send-button" aria-label="Send" title="Send" disabled={!canSend}>
          <Icon name="send" />
        </button>
      </form>
    </Dialog>
  )
}

/** A picked video, shown from the file itself while it goes up. */
function PreviewVideo({ upload }: { upload: PendingUpload }) {
  const url = useMemo(() => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(upload.file) : null), [upload.file])
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url])
  return url ? <video src={url} controls playsInline aria-label={upload.fileName} /> : null
}
